/**
 * Per-project sidebar section and page toggles.
 *
 * Admins can disable whole sections OR individual pages within a section for
 * a project (Project.disabledSections, managed through the Edit Project
 * modal). Section keys ('analysis') hide the whole group; page keys
 * ('analysis.ensembles') hide just that page while its siblings stay
 * available. The Overview section can never be disabled, so it is not listed
 * here. Keys must match Maestro's PROJECT_SECTIONS / PROJECT_SECTION_PAGES in
 * src/models/project.model.ts.
 *
 * New projects start with the specialist modules turned off (Geode, the four
 * Analysis & Economics pages, Compliance, Operation). Maestro owns that list
 * as DEFAULT_DISABLED_SECTIONS and applies it on create; nothing here needs
 * to know it, since the project's stored array arrives with the project.
 */
export const PROJECT_SECTIONS = [
  {
    key: 'management',
    label: 'Management',
    description: 'Tasks & board, task templates',
    pages: [
      { key: 'management.tasks', label: 'Tasks & Board' },
      { key: 'management.templates', label: 'Templates' },
    ],
  },
  {
    key: 'engineering',
    label: 'Heap Leach',
    description: 'Heaps, kinetics, leaching, simulations, results',
    pages: [
      { key: 'engineering.heaps', label: 'Heaps' },
      { key: 'engineering.kinetics', label: 'Kinetics' },
      { key: 'engineering.hydrodynamics', label: 'Hydrodynamics' },
      { key: 'engineering.leaching', label: 'Leaching' },
      { key: 'engineering.run', label: 'Run Simulation' },
      { key: 'engineering.geode', label: 'Geode Models' },
      { key: 'engineering.results', label: 'Results' },
    ],
  },
  {
    key: 'kinetics',
    label: 'Kinetics Tools',
    description: 'Lab-test library, kinetics fitting (also hidden while Heap Leach is disabled)',
    pages: [
      { key: 'kinetics.labTests', label: 'Lab Tests' },
      { key: 'kinetics.fitting', label: 'Kinetics Fitting' },
    ],
  },
  {
    key: 'sitewater',
    label: 'Site & Water',
    description: 'Climate, water balance (also hidden while Heap Leach is disabled)',
    pages: [
      { key: 'sitewater.climate', label: 'Climate' },
      { key: 'sitewater.waterBalance', label: 'Water Balance' },
    ],
  },
  {
    key: 'analysis',
    label: 'Analysis & Economics',
    description: 'NPV & cashflow, ensembles, closure, reconciliation',
    pages: [
      { key: 'analysis.npv', label: 'NPV & Cashflow' },
      { key: 'analysis.ensembles', label: 'Ensembles' },
      { key: 'analysis.closure', label: 'Closure' },
      { key: 'analysis.reconciliation', label: 'Reconciliation' },
    ],
  },
  {
    key: 'compliance',
    label: 'Compliance',
    description: 'Document register, transmittals, approvals',
    pages: [
      { key: 'compliance.documents', label: 'Document Register' },
      { key: 'compliance.transmittals', label: 'Transmittals' },
      { key: 'compliance.approvals', label: 'My Approvals' },
    ],
  },
  {
    key: 'operation',
    label: 'Operation',
    description: 'Operational data, maintenance logs',
    pages: [
      { key: 'operation.operationalData', label: 'Operational Data' },
      { key: 'operation.maintenance', label: 'Maintenance Logs' },
    ],
  },
];

/** Every valid page key, e.g. 'analysis.ensembles'. */
export const PROJECT_PAGE_KEYS = PROJECT_SECTIONS.flatMap((s) =>
  s.pages.map((p) => p.key),
);

/**
 * Keys that moved when Lab Tests and Kinetics Fitting left Heap Leach for
 * their own Kinetics Tools section (2026-09). A project's stored array may
 * still carry the old keys; they are read as the new ones so nothing needs
 * migrating. Maestro keeps the same map and rewrites them on save.
 */
export const LEGACY_PAGE_KEYS = {
  'engineering.labTests': 'kinetics.labTests',
  'engineering.kineticsFitting': 'kinetics.fitting',
};

/** Stored disabledSections → current keys, legacy keys mapped, deduped. */
export const normalizeDisabledSections = (disabledSections) => {
  const list = Array.isArray(disabledSections) ? disabledSections : [];
  return [...new Set(list.map((k) => LEGACY_PAGE_KEYS[k] || k))];
};

export const getDisabledSections = (project) =>
  normalizeDisabledSections(project?.disabledSections);

/**
 * Whether a section is hidden given a project's raw disabledSections array.
 *
 * Site & Water holds pages that lived inside Engineering until the sections
 * split (v0.19), and Kinetics Tools holds pages that lived there until
 * 2026-09: projects that disabled Engineering expected those pages to
 * disappear with it, and no stored disabledSections array from before each
 * split can mention the new section. Coupling both to engineering preserves
 * that behavior without a data migration. Revisit when a facility other than
 * the heap (Tailings/Waste Rock) starts consuming Site & Water pages.
 */
const HIDDEN_WITH_ENGINEERING = ['sitewater', 'kinetics'];

export const isSectionKeyHidden = (disabledSections, sectionKey) => {
  const disabled = normalizeDisabledSections(disabledSections);
  if (disabled.includes(sectionKey)) return true;
  return HIDDEN_WITH_ENGINEERING.includes(sectionKey) && disabled.includes('engineering');
};

/**
 * Whether an individual page is hidden. A page is hidden when its own key is
 * listed OR its parent section is hidden (including the sitewater/engineering
 * coupling above). Page keys are '<section>.<page>'.
 */
export const isPageKeyHidden = (disabledSections, pageKey) => {
  const disabled = normalizeDisabledSections(disabledSections);
  const key = LEGACY_PAGE_KEYS[pageKey] || pageKey;
  const sectionKey = key.split('.')[0];
  if (isSectionKeyHidden(disabled, sectionKey)) return true;
  return disabled.includes(key);
};

export const isSectionDisabled = (project, sectionKey) =>
  isSectionKeyHidden(getDisabledSections(project), sectionKey);

export const isPageDisabled = (project, pageKey) =>
  isPageKeyHidden(getDisabledSections(project), pageKey);
